import { useEffect, useState } from "react";
import { listAllLocations, type StudioLocationsRequestOptions } from "@v0y4ge/api-client";
import { toUILocation, type UILocation } from "./locations-adapter";

/**
 * useLocations — the Locations list data source. Calls the typed api-client
 * `listLocations` choke point (→ MSW mock store in this env / real infra on
 * deploy), validates against LocationListResponse, and maps each row via the
 * adapter. No static seed data.
 *
 * The API base URL is injected by the host (studio-shell provides the dynamic
 * settings base); in the design/preview env the MSW worker intercepts regardless
 * of the value, so a placeholder is fine here and the real base is wired on deploy.
 */
const REQUEST_OPTS: StudioLocationsRequestOptions = {
  baseUrl: (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SETTINGS_API_URL) || "/api",
  surface: "settings",
};
/** Shared request options for ad-hoc locations ops outside the hook (UIR-35). */
export const LOCATIONS_REQUEST_OPTS = REQUEST_OPTS;

export interface LocationsState {
  locations: UILocation[];
  total: number;
  loading: boolean;
  error: string | null;
}

export function useLocations(): LocationsState & { reload: () => void } {
  const [state, setState] = useState<LocationsState>({ locations: [], total: 0, loading: true, error: null });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    listAllLocations({ limit: 200 }, { ...REQUEST_OPTS, signal: controller.signal })
      .then((res) => {
        if (cancelled) return;
        const rows = (res.items ?? res.data ?? res.locations ?? []).map(toUILocation);
        const total = res.locationsPagination?.total ?? res.pagination?.total ?? rows.length;
        setState({ locations: rows, total, loading: false, error: null });
      })
      .catch((e: unknown) => {
        if (cancelled || (e instanceof DOMException && e.name === "AbortError")) return;
        setState({ locations: [], total: 0, loading: false, error: e instanceof Error ? e.message : "Failed to load locations." });
      });
    return () => { cancelled = true; controller.abort(); };
  }, [nonce]);

  return { ...state, reload: () => setNonce((n) => n + 1) };
}
